import { z } from 'zod';

/**
 * Erro padronizado — RFC 7807 (Problem Details), conforme `.spec/001`, seção 3.
 *
 * `code` é a chave estável que o frontend usa para decidir o que fazer
 * (destacar campo, abrir diálogo). `detail` é a mensagem em pt-BR exibida ao
 * usuário. Tratar erro comparando o texto da mensagem quebra a cada ajuste de
 * redação — por isso o código existe.
 */

export const erroCampoSchema = z.object({
  path: z.string(),
  code: z.string(),
  mensagem: z.string().optional(),
});

export const problemDetailsSchema = z.object({
  type: z.string().url(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string(),
  /** Correlaciona logs, Sentry e audit_log (.spec/008, seção 9). */
  requestId: z.string().optional(),
  errors: z.array(erroCampoSchema).optional(),
});

export type ProblemDetails = z.infer<typeof problemDetailsSchema>;

export const BASE_TIPO_ERRO = 'https://api.construtora.app/errors';

/**
 * Códigos de erro de negócio.
 *
 * Centralizados aqui para que a API e o frontend usem exatamente a mesma
 * string. Um código escrito à mão nos dois lados diverge na primeira renomeação.
 */
export const CODIGOS_ERRO = {
  // Genéricos
  VALIDACAO: 'validation_error',
  NAO_AUTENTICADO: 'nao_autenticado',
  SEM_PERMISSAO: 'sem_permissao',
  NAO_ENCONTRADO: 'nao_encontrado',
  CONFLITO: 'conflito',
  LIMITE_REQUISICOES: 'limite_requisicoes',

  /*
    Idempotência (.spec/001, seção 3). Os três são do plugin, não de módulo
    nenhum: qualquer escrita pode recebê-los.

    `REQUISICAO_EM_ANDAMENTO` é o único dos três que o cliente pode tratar
    sozinho — é a resposta ao duplo clique, e a tela certa a dar é a de "já
    estamos gravando", não a de erro. Os outros dois são defeito de quem chamou.
  */
  CHAVE_IDEMPOTENTE_INVALIDA: 'chave_idempotente_invalida',
  CHAVE_IDEMPOTENTE_REUTILIZADA: 'chave_idempotente_reutilizada',
  REQUISICAO_EM_ANDAMENTO: 'requisicao_em_andamento',

  // Rateio (.spec/015, item 2)
  RATEIO_SOMA: 'sum_must_be_100',
  RATEIO_SEM_ITENS: 'rateio_sem_itens',
  INDICE_RATEIO_INVALIDO: 'indice_rateio_invalido',

  // Estoque (.spec/010)
  ESTOQUE_INSUFICIENTE: 'estoque_insuficiente',
  QUANTIDADE_INVALIDA: 'quantidade_invalida',

  // Nota de compra
  NOTA_JA_CONFIRMADA: 'nota_ja_confirmada',
  EXISTE_MOVIMENTO_POSTERIOR: 'existe_movimento_posterior',
  EXISTE_CONSUMO_POSTERIOR: 'existe_consumo_posterior',
  TITULO_JA_BAIXADO: 'titulo_ja_baixado',

  // Financeiro
  VALOR_BAIXA_EXCEDE_SALDO: 'valor_baixa_excede_saldo',
  SALDO_EXCEDE_VALOR_TITULO: 'saldo_excede_valor_titulo',
  BAIXA_JA_ESTORNADA: 'baixa_ja_estornada',
  PARAM_HISTORICO_JUROS: 'parametro_historico_juros_nao_configurado',
  PARAM_HISTORICO_OUTROS: 'parametro_historico_outros_nao_configurado',
  PARAM_HISTORICO_DESCONTO: 'parametro_historico_desconto_nao_configurado',

  // Compras (.spec/015, itens 1 e 5)
  QUANTIDADE_EXCEDE_SOLICITADO: 'quantidade_excede_solicitado',
  SOLICITACAO_JA_ATENDIDA: 'solicitacao_ja_atendida',
  SOLICITACAO_ENCERRADA: 'solicitacao_encerrada',
  SOLICITACAO_ATENDIMENTO_PARCIAL: 'solicitacao_com_atendimento_parcial',
  MOTIVO_OBRIGATORIO: 'motivo_obrigatorio',

  // Segurança (.spec/005, seção 3)
  SEGREGACAO_FUNCOES: 'segregacao_funcoes',

  // Mobile (.spec/014)
  INSUMO_INATIVO: 'insumo_inativo',
  VERSAO_APP_DEFASADA: 'versao_app_defasada',
} as const;

export type CodigoErro = (typeof CODIGOS_ERRO)[keyof typeof CODIGOS_ERRO];
